import React, { useMemo, useState, useCallback } from 'react';
import Table from '../../components/Table';
import Button from '../../components/Button';
import PlusIcon from 'assets/images/icon/plus-white.png'
import Input from '../../components/Input';
import search from 'assets/images/icon/search-normal.png';
import ToggleSwitch from '../../components/ToggleSwitch';
import Card from '../../components/Card';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import styles from "./benefits.module.scss";
import { cn, stringMerger } from 'utils';
import CompanyLogo from 'assets/images/icon/company-logo';
import UsersLogo from 'assets/images/icon/users-logo';
import { getCompanyColumns } from './columns';
import PersonIcon from 'assets/images/icon/person-icon';
import PersonsIcon from 'assets/images/icon/persons-icon';
import UserChecked from 'assets/images/icon/user-checked';
import UserDisabled from 'assets/images/icon/user-disabled';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCompanyBenefits, getEmployeeBenefits, updateCompanyBenefit, updateEmployeeBenefit } from 'services/benefits';
import { getEmployees } from 'services/employees';
import ConfirmModal from 'components/ConfirmModal';
import { getActiveCompanyId } from 'utils/companyContext';

function Benefits() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [selectedTab, setSelectedTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewBenefit, setViewBenefit] = useState(null);
    const [showInactive, setShowInactive] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);
    const hasCompany = !!getActiveCompanyId();

    const { data: companyBenefitsRawApi, isLoading: loadingCompany } = useQuery({
        queryKey: ['company-benefits'],
        queryFn: getCompanyBenefits,
        enabled: hasCompany,
    });

    const { data: employeeBenefitsRawApi, isLoading: loadingEmployee } = useQuery({
        queryKey: ['employee-benefits'],
        queryFn: getEmployeeBenefits,
        enabled: hasCompany,
    });

    const { data: employeesRawApi } = useQuery({
        queryKey: ['employees'],
        queryFn: getEmployees,
        enabled: hasCompany,
    });

        const companyBenefitsRaw = useMemo(() => Array.isArray(companyBenefitsRawApi) ? companyBenefitsRawApi : (companyBenefitsRawApi?.data ?? []), [companyBenefitsRawApi]);
    const employeeBenefitsRaw = useMemo(() => Array.isArray(employeeBenefitsRawApi) ? employeeBenefitsRawApi : (employeeBenefitsRawApi?.data ?? []), [employeeBenefitsRawApi]);
    const employeesRaw = useMemo(() => Array.isArray(employeesRawApi) ? employeesRawApi : (employeesRawApi?.data ?? []), [employeesRawApi]);

    const { mutate: mutateCompanyBenefit } = useMutation({
        mutationFn: updateCompanyBenefit,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-benefits'] }),
    });

    const { mutate: mutateEmployeeBenefit } = useMutation({
        mutationFn: updateEmployeeBenefit,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['employee-benefits'] }),
    });

    const handleToggleCompanyBenefit = useCallback((d) => {
        const raw = companyBenefitsRaw.find(b => b.id === d.id);
        if (!raw) return;
        const isDeactivating = raw.active !== false;
        setConfirmModal({
            title: isDeactivating ? 'Deactivate Benefit Plan' : 'Activate Benefit Plan',
            message: isDeactivating
                ? 'This plan will be marked inactive. It will no longer be used when enrolling employees and will be excluded from payroll calculations.'
                : 'This plan will be marked active again and included in payroll calculations.',
            confirmLabel: isDeactivating ? 'Deactivate' : 'Activate',
            confirmVariant: isDeactivating ? 'danger' : 'success',
            onConfirm: () => {
                mutateCompanyBenefit({ benefitId: d.id, active: !isDeactivating, period: raw.period });
                setConfirmModal(null);
            },
        });
    }, [companyBenefitsRaw, mutateCompanyBenefit]);

    const handleToggleEmployeeBenefit = useCallback((benefitId, currentActive) => {
        const isDeactivating = currentActive !== false;
        setConfirmModal({
            title: isDeactivating ? 'Deactivate Enrollment' : 'Activate Enrollment',
            message: isDeactivating
                ? 'This benefit enrollment will be marked inactive. It will no longer be deducted from this employee\'s paycheck.'
                : 'This enrollment will be re-activated and deductions will resume in future payrolls.',
            confirmLabel: isDeactivating ? 'Deactivate' : 'Activate',
            confirmVariant: isDeactivating ? 'danger' : 'success',
            onConfirm: () => {
                mutateEmployeeBenefit({ benefitId, active: !isDeactivating });
                setConfirmModal(null);
            },
        });
    }, [mutateEmployeeBenefit]);

    const employeeMap = useMemo(() => {
        const map = {};
        employeesRaw.forEach(e => { map[e.id] = `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.id; });
        return map;
    }, [employeesRaw]);
    const benefitTypeMap = useMemo(() => {
        const map = {};
        companyBenefitsRaw.forEach(b => { map[b.id] = b.type || b.description || b.id; });
        return map;
    }, [companyBenefitsRaw]);

    const benefitPeriodMap = useMemo(() => {
        const map = {};
        companyBenefitsRaw.forEach(b => { map[b.id] = b.period; });
        return map;
    }, [companyBenefitsRaw]);

    const benefitDateMap = useMemo(() => {
        const map = {};
        companyBenefitsRaw.forEach(b => { map[b.id] = { start: b.effective, end: b.effectiveEnd }; });
        return map;
    }, [companyBenefitsRaw]);
    const WAITING_LABELS = { Immediate: 'Immediate', FirstOfMonthAfterHire: '1st of month after hire', '30days': '30 days', '60days': '60 days', '90days': '90 days' };

    const companyData = useMemo(() => companyBenefitsRaw
        .filter(b => {
            if (!(b.type || b.description)) return false;
            return showInactive ? b.active === false : b.active !== false;
        })
        .map(b => ({
            id: b.id,
            type: b.type || '—',
            period: b.period || '—',
            start_date: b.effective ? new Date(b.effective).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—',
            end_date: b.effectiveEnd ? new Date(b.effectiveEnd).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—',
            company_contribution: b.companyContributionPercent != null && b.companyContributionPercent > 0 ? `${b.companyContributionPercent}%` : b.companyContributionAmount != null && b.companyContributionAmount > 0 ? `$${b.companyContributionAmount}` : '—',
            description: b.description || '—',
            preTax: b.preTax,
            waitingPeriod: b.waitingPeriod,
            documentUrl: b.documentUrl,
            documents: (() => { try { return b.documents ? JSON.parse(b.documents) : null; } catch { return null; } })(),
        })), [companyBenefitsRaw, showInactive]);

    const employeeData = useMemo(() => employeeBenefitsRaw
        .filter(b => b.employeeId)
        .map(b => ({
            id: b.id,
            employee: employeeMap[b.employeeId] || b.employeeId || '—',
            type: b.type || benefitTypeMap[b.companyBenefitId] || b.companyBenefitId || '—',
            period: b.period || benefitPeriodMap[b.companyBenefitId] || '—',
            start_date: b.effectiveStart ? new Date(b.effectiveStart).toLocaleDateString('en-US', { timeZone: 'UTC' }) : (benefitDateMap[b.companyBenefitId]?.start ? new Date(benefitDateMap[b.companyBenefitId].start).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—'),
            end_date: b.effectiveEnd ? new Date(b.effectiveEnd).toLocaleDateString('en-US', { timeZone: 'UTC' }) : (benefitDateMap[b.companyBenefitId]?.end ? new Date(benefitDateMap[b.companyBenefitId].end).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—'),
            company_contribution: b.companyContributionPercent != null && b.companyContributionPercent > 0
                ? `${b.companyContributionPercent}%`
                : b.companyContribution != null && b.companyContribution > 0
                    ? `$${b.companyContribution}`
                    : '—',
            employee_contribution: b.employeeContributionPercent != null && b.employeeContributionPercent > 0
                ? `${b.employeeContributionPercent}%`
                : b.employeeDeduction != null && b.employeeDeduction > 0
                    ? `$${b.employeeDeduction}`
                    : '—',
            description: b.description || '—',
        })), [employeeBenefitsRaw, employeeMap, benefitTypeMap, benefitPeriodMap, benefitDateMap]);

    const activeCompanyBenefits = companyBenefitsRaw.filter(b => b.active).length;
    const employeesWithBenefits = new Set(employeeBenefitsRaw.filter(b => b.employeeId).map(b => b.employeeId)).size;
    const pendingReviewCount = employeeBenefitsRaw.filter(b => b.pendingReview === true && b.active !== false).length;

    // Group employee benefits by employee for the grouped UI
    const groupedEmployeeData = useMemo(() => {
        const groups = {};
        employeeBenefitsRaw.filter(b => b.employeeId && (showInactive ? b.active === false : b.active !== false)).forEach(b => {
            if (!groups[b.employeeId]) {
                groups[b.employeeId] = {
                    employeeId: b.employeeId,
                    employeeName: employeeMap[b.employeeId] || 'Unknown Employee',
                    benefits: [],
                };
            }
            groups[b.employeeId].benefits.push({
                id: b.id,
                raw: b,
                type: b.type || benefitTypeMap[b.companyBenefitId] || '—',
                period: b.period || benefitPeriodMap[b.companyBenefitId] || '—',
                start_date: b.effectiveStart
                    ? new Date(b.effectiveStart).toLocaleDateString('en-US', { timeZone: 'UTC' })
                    : (benefitDateMap[b.companyBenefitId]?.start
                        ? new Date(benefitDateMap[b.companyBenefitId].start).toLocaleDateString('en-US', { timeZone: 'UTC' })
                        : '—'),
                employee_contribution: b.employeeContributionPercent != null && b.employeeContributionPercent > 0
                    ? `${b.employeeContributionPercent}%`
                    : b.employeeDeduction != null && b.employeeDeduction > 0 ? `$${b.employeeDeduction}`
                    : b.employeeContributionAmount != null && b.employeeContributionAmount > 0 ? `$${b.employeeContributionAmount}` : '—',
                description: b.description || '—',
            });
        });
        return Object.values(groups);
    }, [employeeBenefitsRaw, employeeMap, benefitTypeMap, benefitPeriodMap, benefitDateMap, showInactive]);

    const [expandedEmployees, setExpandedEmployees] = useState(() => new Set());
    const toggleEmployee = useCallback((id) => {
        setExpandedEmployees(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const filteredGroupedData = useMemo(() => {
        if (!searchTerm.trim()) return groupedEmployeeData;
        const term = searchTerm.toLowerCase();
        return groupedEmployeeData
            .map(g => ({
                ...g,
                benefits: g.benefits.filter(b =>
                    b.type.toLowerCase().includes(term) ||
                    g.employeeName.toLowerCase().includes(term)
                ),
            }))
            .filter(g => g.employeeName.toLowerCase().includes(term) || g.benefits.length > 0);
    }, [groupedEmployeeData, searchTerm]);

    const companyCards = [
        { title: 'Company Benefits',   count: companyData.length,        icon: <PersonIcon />,   tab: 0    },
        { title: 'Employee Benefits',  count: employeeData.length,       icon: <PersonsIcon />,  tab: 1    },
        { title: 'Active Benefits',    count: activeCompanyBenefits,     icon: <UserChecked />,  tab: null },
        { title: 'Employee w/Benefits',count: employeesWithBenefits,     icon: <UserDisabled />, tab: null },
    ];

    const companyColumns = useMemo(() => getCompanyColumns(), []);
    const isLoading = selectedTab === 0 ? loadingCompany : loadingEmployee;

    return (
        <>
        <div className={cn('content', styles.benefitsContainer)}>
            <div className="start-new-payroll-section">
                <div style={{ padding: "16px 24px 16px 16px" }}>
                    <div className="row mt-4">
                        {companyCards.map((item) => (
                            <Card
                                key={item.title}
                                title={item.title}
                                count={item.count}
                                icon={item.icon}
                                isImage={false}
                                isActive={item.tab !== null && selectedTab === item.tab}
                                onClick={item.tab !== null ? () => setSelectedTab(item.tab) : undefined}
                            />
                        ))}
                    </div>
                </div>
                <div className={styles.tabList}>
                    <button
                        className={cn(styles.tab, selectedTab === 0 ? styles.active : '')}
                        onClick={() => setSelectedTab(0)}
                    >
                        <CompanyLogo color={selectedTab === 0 ? "#1B75F0" : "#5F5F5F"} />
                        <p>Benefit Plans</p>
                    </button>
                    <button
                        className={cn(styles.tab, selectedTab === 1 ? styles.active : '')}
                        onClick={() => setSelectedTab(1)}
                    >
                        <UsersLogo color={selectedTab === 1 ? "#1B75F0" : "#5F5F5F"} />
                        <p>Enrollments{pendingReviewCount > 0 && <span style={{ marginLeft: 6, background: '#1B75F0', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, verticalAlign: 'middle' }}>{pendingReviewCount}</span>}</p>
                    </button>
                </div>
                <div className={cn('container-spacing')}>
                    <div className='d-flex justify-content-between mt-4 mb-3' style={{ flexWrap: 'wrap', gap: 8 }}>
                        <div className='d-flex align-items-center gap-3'>
                            {selectedTab === 0 ? (
                                <Button
                                    startIcon={<img src={PlusIcon} alt="add" />}
                                    onClick={() => navigate(stringMerger(routes.BENEFITS.HOME, routes.BENEFITS.ADD_EDIT_COMPANY_BENEFIT))}
                                >
                                    Add Benefit Plan
                                </Button>
                            ) : (
                                <Button
                                    startIcon={<img src={PlusIcon} alt="add" />}
                                    onClick={() => navigate(stringMerger(routes.BENEFITS.HOME, routes.BENEFITS.ADD_EDIT_EMPLOYEE_BENEFIT))}
                                >
                                    Enroll Employee
                                </Button>
                            )}
                            <ToggleSwitch checked={showInactive} onChange={setShowInactive} />
                        </div>
                        <Input
                            placeholder='Search by Name, Email, or Id'
                            startIcon={search}
                            style={{ width: "100%", maxWidth: "380px" }}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {isLoading ? (
                        <p className="text-center mt-4">Loading benefits...</p>
                    ) : selectedTab === 0 ? (
                        <Table
                            headers={companyColumns}
                            data={companyData.map(d => ({ ...d, active: companyBenefitsRaw.find(b => b.id === d.id)?.active }))}
                            handleView={(d) => setViewBenefit(companyBenefitsRaw.find(b => b.id === d.id))}
                            handleEdit={(d) => navigate(`/benefits/add-edit-company-benefit?id=${d.id}`)}
                            handleToggleActive={handleToggleCompanyBenefit}
                        />
                    ) : filteredGroupedData.length === 0 ? (
                        <div className={styles.emptyState}>No employee benefits found.</div>
                    ) : (
                        <div className={styles.employeeGroupList}>
                            {filteredGroupedData.map(group => {
                                const isOpen = expandedEmployees.has(group.employeeId);
                                const initials = (group.employeeName || '?').split(' ').filter(w => w).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
                                const employerPaidTypes = ['lifeinsurance', 'std', 'ltd'];
                                const warningCount = group.benefits.filter(b =>
                                    b.employee_contribution === '—' &&
                                    !employerPaidTypes.includes((b.type || '').toLowerCase())
                                ).length;
                                const pendingCount = group.benefits.filter(b => b.raw?.pendingReview === true).length;
                                return (
                                    <div key={group.employeeId} className={styles.employeeGroup}>
                                        <div
                                            className={cn(styles.employeeGroupHeader, isOpen ? styles.open : '')}
                                            onClick={() => toggleEmployee(group.employeeId)}
                                        >
                                            <div className={styles.employeeAvatar}>{initials}</div>
                                            <span className={styles.employeeNameText}>{group.employeeName}</span>
                                            <span className={styles.benefitCountBadge}>
                                                {group.benefits.length} benefit{group.benefits.length !== 1 ? 's' : ''}
                                            </span>
                                            {warningCount > 0 && (
                                                <span title={`${warningCount} benefit(s) have no deduction amount set`} style={{ background: '#fff3cd', color: '#856404', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, border: '1px solid #ffc107' }}>
                                                    ⚠ {warningCount} missing deduction
                                                </span>
                                            )}
                                            {pendingCount > 0 && (
                                                <span title={`${pendingCount} self-enrolled benefit(s) need employer contribution`} style={{ background: '#fff3cd', color: '#92400e', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, border: '1px solid #f59e0b' }}>
                                                    ⚠ {pendingCount} need{pendingCount === 1 ? 's' : ''} review
                                                </span>
                                            )}
                                            <span className={cn(styles.chevron, isOpen ? styles.rotated : '')}>▼</span>
                                        </div>
                                        {isOpen && (
                                            <div style={{ padding: '0 16px 16px' }}>
                                                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 500 }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 12 }}>Benefit Type</th>
                                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 12 }}>Source</th>
                                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 12 }}>Period</th>
                                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 12 }}>Start Date</th>
                                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 12 }}>Employee Deduction</th>
                                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 12 }}>Employer Contribution</th>
                                                            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#555', fontSize: 12 }}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {group.benefits.map((b, i) => {
                                                            const isOnboarded = b.description === 'Enrolled during onboarding' || (b.raw?.description || '').toLowerCase().includes('enrolled during onboarding');
                                                            const isPendingReview = b.raw?.pendingReview === true;
                                                            const isEmployerPaid = ['lifeinsurance', 'std', 'ltd'].includes((b.type || '').toLowerCase());
                                                            const hasDeduction = b.employee_contribution !== '—' || isEmployerPaid;
                                                            const typeColors = { Medical: '#e3f2fd', Dental: '#e8f5e9', Vision: '#f3e5f5', '401k': '#fff3e0', HSA: '#e0f7fa', FSA: '#fce4ec', LifeInsurance: '#f3e5f5', Disability: '#ede7f6', Other: '#f5f5f5' };
                                                            const typeBg = typeColors[b.type] || '#f5f5f5';
                                                            return (
                                                                <tr key={b.id} style={{ borderBottom: '1px solid #f0f0f0', background: isPendingReview ? '#fffbf0' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                    <td style={{ padding: '10px 12px' }}>
                                                                        <span style={{ background: typeBg, padding: '3px 10px', borderRadius: 12, fontWeight: 600, fontSize: 12 }}>{b.type}</span>
                                                                    </td>
                                                                    <td style={{ padding: '10px 12px' }}>
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                            {isOnboarded
                                                                                ? <span style={{ background: '#e8f4fd', color: '#1565C0', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>Self-Onboarded</span>
                                                                                : <span style={{ background: '#e8f5e9', color: '#2e7d32', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>Manual</span>
                                                                            }
                                                                            {isPendingReview && (
                                                                                <span style={{ background: '#fff3cd', color: '#856404', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, border: '1px solid #f59e0b' }}>⚠ Needs Review</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '10px 12px', color: '#555' }}>{b.period !== '—' ? b.period : <span style={{ color: '#bbb' }}>—</span>}</td>
                                                                    <td style={{ padding: '10px 12px', color: '#555' }}>{b.start_date !== '—' ? b.start_date : <span style={{ color: '#bbb' }}>—</span>}</td>
                                                                    <td style={{ padding: '10px 12px' }}>
                                                                        {isEmployerPaid
                                                                            ? <span style={{ color: '#bbb' }}>—</span>
                                                                            : hasDeduction
                                                                                ? <span style={{ color: '#c62828', fontWeight: 600 }}>{b.employee_contribution}</span>
                                                                                : <span style={{ background: '#fff3cd', color: '#856404', fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 600 }}>⚠ Not set</span>
                                                                        }
                                                                    </td>
                                                                    <td style={{ padding: '10px 12px' }}>
                                                                        {(() => {
                                                                            const pct = b.raw?.companyContributionPercent;
                                                                            const amt = b.raw?.companyContribution;
                                                                            if (pct != null && pct > 0)
                                                                                return <span style={{ color: '#2e7d32', fontWeight: 600 }}>{pct}%</span>;
                                                                            if (amt != null && amt > 0)
                                                                                return <span style={{ color: '#2e7d32', fontWeight: 600 }}>${amt}</span>;
                                                                            return <span style={{ color: isPendingReview ? '#f59e0b' : '#bbb', fontSize: 11, fontWeight: isPendingReview ? 700 : 400 }}>{isPendingReview ? '⚠ Not set' : '—'}</span>;
                                                                        })()}
                                                                    </td>
                                                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
                                                                            <button title="View" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }} onClick={(e) => { e.stopPropagation(); setViewBenefit(b.raw); }}>
                                                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                                                            </button>
                                                                            {isPendingReview && (
                                                                                <button
                                                                                    title="Review & Approve"
                                                                                    style={{ background: '#1B75F0', border: 'none', cursor: 'pointer', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}
                                                                                    onClick={(e) => { e.stopPropagation(); navigate(`/benefits/add-edit-employee-benefit?id=${b.id}&returnTo=/benefits`); }}
                                                                                >
                                                                                    ✓ Review
                                                                                </button>
                                                                            )}
                                                                    <button title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }} onClick={(e) => { e.stopPropagation(); navigate(`/benefits/add-edit-employee-benefit?id=${b.id}`); }}>
                                                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                                            </button>
                                                                            <button
                                                                                title={b.raw?.active === false ? 'Activate' : 'Deactivate'}
                                                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                                                onClick={(e) => { e.stopPropagation(); handleToggleEmployeeBenefit(b.id, b.raw?.active); }}
                                                                            >
                                                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={b.raw?.active === false ? '#2e7d32' : '#c62828'} strokeWidth="2.2"><path d="M18.36 6.64A9 9 0 1 1 5.64 5.64"/><line x1="12" y1="2" x2="12" y2="12"/></svg>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
        <ConfirmModal
            isOpen={!!confirmModal}
            title={confirmModal?.title}
            message={confirmModal?.message}
            confirmLabel={confirmModal?.confirmLabel}
            confirmVariant={confirmModal?.confirmVariant}
            onConfirm={confirmModal?.onConfirm}
            onCancel={() => setConfirmModal(null)}
        />
        {viewBenefit && (
            <div className="modal show d-block" style={{background:"rgba(0,0,0,0.5)"}} onClick={() => setViewBenefit(null)}>
                <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="modal-content p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Benefit Details</h5>
                            <button className="btn-close" onClick={() => setViewBenefit(null)} />
                        </div>
                        <div className="row g-3">
                            <div className="col-6"><small className="text-muted">Type</small><p className="fw-bold">{viewBenefit.type || benefitTypeMap[viewBenefit.companyBenefitId] || "—"}</p></div>
                            <div className="col-6"><small className="text-muted">Period</small><p className="fw-bold">{viewBenefit.period || benefitPeriodMap[viewBenefit.companyBenefitId] || "—"}</p></div>
                            <div className="col-6"><small className="text-muted">Start Date</small><p className="fw-bold">{(viewBenefit.effective || benefitDateMap[viewBenefit.companyBenefitId]?.start) ? new Date(viewBenefit.effective || benefitDateMap[viewBenefit.companyBenefitId]?.start).toLocaleDateString('en-US', { timeZone: 'UTC' }) : "—"}</p></div>
                            <div className="col-6"><small className="text-muted">End Date</small><p className="fw-bold">{viewBenefit.effectiveEnd ? new Date(viewBenefit.effectiveEnd).toLocaleDateString('en-US', { timeZone: 'UTC' }) : "—"}</p></div>
                            <div className="col-6"><small className="text-muted">Description</small><p className="fw-bold">{viewBenefit.description || "—"}</p></div>
                            <div className="col-6"><small className="text-muted">Company Contribution</small><p className="fw-bold">{(() => { const lp = companyBenefitsRaw.find(b => b.id === viewBenefit.companyBenefitId); const pct = viewBenefit.companyContributionPercent || lp?.companyContributionPercent; const amt = viewBenefit.companyContributionAmount ?? viewBenefit.companyContribution ?? lp?.companyContributionAmount; return pct > 0 ? `${pct}%` : amt > 0 ? `$${amt}` : "—"; })()}</p></div>
                            <div className="col-6"><small className="text-muted">Employee Contribution</small><p className="fw-bold">{viewBenefit.employeeContributionPercent > 0 ? `${viewBenefit.employeeContributionPercent}%` : (viewBenefit.employeeDeduction ?? viewBenefit.employeeContributionAmount) > 0 ? `$${viewBenefit.employeeDeduction ?? viewBenefit.employeeContributionAmount}` : "—"}</p></div>
                            {(() => { const lp = companyBenefitsRaw.find(b => b.id === viewBenefit.companyBenefitId); const empPct = viewBenefit.employeeContributionPercent || lp?.employeeContributionPercent; const premium = viewBenefit.totalPremiumAmount ?? lp?.totalPremiumAmount; if (!empPct || !premium) return null; const perPeriod = (parseFloat(premium) * parseFloat(empPct) / 100).toFixed(2); return (<><div className="col-6"><small className="text-muted">Total Plan Premium / Period</small><p className="fw-bold">${parseFloat(premium).toFixed(2)}</p></div><div className="col-6"><small className="text-muted">Est. Employee Deduction / Period</small><p className="fw-bold" style={{ color: '#1B75F0' }}>${perPeriod}</p></div></>); })()}
                            {viewBenefit.employeeId && <div className="col-6"><small className="text-muted">Employee</small><p className="fw-bold">{employeeMap[viewBenefit.employeeId] || viewBenefit.employeeId}</p></div>}
                            {viewBenefit.preTax != null && <div className="col-6"><small className="text-muted">Tax Treatment</small><p className="fw-bold" style={{ color: viewBenefit.preTax !== false ? '#16a34a' : '#6b7280' }}>{viewBenefit.preTax !== false ? 'Pre-tax' : 'Post-tax'}</p></div>}
                            {viewBenefit.waitingPeriod && <div className="col-6"><small className="text-muted">Waiting Period</small><p className="fw-bold">{WAITING_LABELS[viewBenefit.waitingPeriod] || viewBenefit.waitingPeriod}</p></div>}
                            {(() => {
                                const cleanNotes = viewBenefit.notes
                                    ? viewBenefit.notes
                                        .replace(/\s*\|\s*documents:\[[\s\S]*?\](\s*\|)?/gi, '')
                                        .replace(/^documents:\[[\s\S]*?\](\s*\|)?/gi, '')
                                        .trim().replace(/\s*\|\s*$/, '').trim()
                                    : '';
                                return cleanNotes ? <div className="col-12"><small className="text-muted">Notes</small><p className="fw-bold" style={{ whiteSpace: 'pre-wrap' }}>{cleanNotes}</p></div> : null;
                            })()}
                            {(() => {
                                const DOC_LABELS = { SBC: 'Summary of Benefits & Coverage', PlanDoc: 'Plan Document', EnrollmentGuide: 'Enrollment Guide', NetworkDirectory: 'Network Directory', Formulary: 'Drug Formulary', Other: 'Other' };
                                const rawDocs = viewBenefit.documents;
                                const parsedDocs = Array.isArray(rawDocs) ? rawDocs
                                    : (typeof rawDocs === 'string' && rawDocs.trim()) ? (() => { try { return JSON.parse(rawDocs); } catch { return []; } })()
                                    : [];
                                const docs = parsedDocs.length > 0
                                    ? parsedDocs
                                    : viewBenefit.documentUrl
                                        ? [{ type: 'PlanDoc', url: viewBenefit.documentUrl, name: 'View Document' }]
                                        : [];
                                if (docs.length === 0) return null;
                                return (
                                    <div className="col-12">
                                        <small className="text-muted">Plan Documents</small>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                                            {docs.map((doc, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1B75F0', background: '#EFF6FF', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                                                        {DOC_LABELS[doc.type] || doc.type}
                                                    </span>
                                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1B75F0', fontWeight: 600, fontSize: 13 }}>
                                                        📄 {doc.name || 'View Document'} ↗
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

export default Benefits;
















